import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useBilling, useOpenBillingPortal, useStartCheckout } from '../api/useBilling';
import type { BillingStatus } from '../types/billing';

const statusCopy: Record<BillingStatus, { label: string; detail: string; tone: string }> = {
  NOT_CONFIGURED: {
    label: 'Not configured',
    detail: 'This organization has not started Stripe subscription billing yet.',
    tone: 'neutral',
  },
  PENDING: {
    label: 'Pending',
    detail: 'Stripe Checkout has started. Route42 is waiting for confirmed subscription state.',
    tone: 'info',
  },
  TRIALING: {
    label: 'Trialing',
    detail: 'The Stripe subscription is currently in a trial period.',
    tone: 'info',
  },
  ACTIVE: {
    label: 'Active',
    detail: 'Subscription billing is active for this organization.',
    tone: 'success',
  },
  PAST_DUE: {
    label: 'Past due',
    detail: 'Stripe reported an unpaid invoice. Route42 access continues while the billing issue is resolved.',
    tone: 'warning',
  },
  PAYMENT_ACTION_REQUIRED: {
    label: 'Action required',
    detail: 'Stripe needs additional payment action. Route42 access continues while billing is resolved.',
    tone: 'warning',
  },
  CANCELED: {
    label: 'Canceled',
    detail: 'The Stripe subscription is canceled. Route42 tenant lifecycle remains a separate administrative decision.',
    tone: 'danger',
  },
};

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Route42 could not complete the billing request.';
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function intervalLabel(value: string | null | undefined): string {
  if (!value) return 'Recurring';
  if (value.toLowerCase() === 'month') return 'Monthly';
  if (value.toLowerCase() === 'year') return 'Annual';
  return value[0].toUpperCase() + value.slice(1);
}

function planLabel(planId: string): string {
  if (planId.toLowerCase() === 'mvp') return 'Route42 MVP';
  return planId;
}

function graceMessage(value: string | null | undefined): string {
  if (!value) return 'Route42 has not recorded a commercial grace deadline yet.';
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return 'Route42 has not recorded a commercial grace deadline yet.';
  const formatted = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(end);
  if (end.getTime() < Date.now()) {
    return `The commercial grace period ended ${formatted}. Route42 access remains available pending explicit commercial review.`;
  }
  return `Commercial grace period through ${formatted}. Use Customer Portal to resolve the payment issue.`;
}

export function BillingPage() {
  const auth = useAuth();
  const billingQuery = useBilling();
  const checkoutMutation = useStartCheckout();
  const portalMutation = useOpenBillingPortal();
  const [searchParams] = useSearchParams();

  const returnNotice = useMemo(() => {
    const checkout = searchParams.get('billing_checkout');
    if (checkout === 'success') return { tone: 'success', text: 'Checkout completed. Route42 is confirming the subscription with Stripe.' };
    if (checkout === 'canceled') return { tone: 'neutral', text: 'Checkout was canceled. No billing change was applied by the browser.' };
    if (searchParams.get('billing_portal') === 'returned') return { tone: 'neutral', text: 'Returned from Stripe Customer Portal. Billing state is shown below.' };
    return null;
  }, [searchParams]);

  if (!auth.can(permissions.billingRead)) {
    return (
      <section className="r42-page r42-page--wide">
        <div className="r42-empty-state">
          <i className="bi bi-shield-lock" aria-hidden="true" />
          <h2>Billing is not available</h2>
          <p>Your current Route42 membership does not include permission to view organization billing.</p>
        </div>
      </section>
    );
  }

  const billing = billingQuery.data;
  const status = billing ? statusCopy[billing.status] : null;
  const busy = checkoutMutation.isPending || portalMutation.isPending;
  const actionError = checkoutMutation.error ?? portalMutation.error;

  async function startCheckout() {
    checkoutMutation.reset();
    portalMutation.reset();
    try {
      const result = await checkoutMutation.mutateAsync();
      window.location.assign(result.checkout_session.url);
    } catch {
      // Mutation state renders the user-safe error.
    }
  }

  async function openPortal() {
    checkoutMutation.reset();
    portalMutation.reset();
    try {
      const result = await portalMutation.mutateAsync();
      window.location.assign(result.portal_session.url);
    } catch {
      // Mutation state renders the user-safe error.
    }
  }

  return (
    <section className="r42-page r42-page--wide r42-billing-page">
      <div className="r42-page-header r42-billing-header">
        <div>
          <span className="r42-eyebrow">Organization settings</span>
          <h1>Billing</h1>
          <p>Review Route42 subscription state and use Stripe for secure payment and billing management.</p>
        </div>
      </div>

      {returnNotice ? (
        <div className={`r42-billing-notice r42-billing-notice--${returnNotice.tone}`} role="status">
          <i className={`bi ${returnNotice.tone === 'success' ? 'bi-check-circle' : 'bi-info-circle'}`} aria-hidden="true" />
          <span>{returnNotice.text}</span>
        </div>
      ) : null}

      {billingQuery.isPending ? (
        <div className="r42-billing-grid" aria-label="Loading billing information">
          <div className="r42-billing-card r42-billing-card--primary"><div className="r42-billing-skeleton" /><div className="r42-billing-skeleton is-short" /></div>
          <div className="r42-billing-card"><div className="r42-billing-skeleton" /><div className="r42-billing-skeleton is-short" /></div>
        </div>
      ) : billingQuery.isError ? (
        <div className="r42-inline-state r42-inline-state--error">
          <span>{errorText(billingQuery.error)}</span>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => void billingQuery.refetch()}>Try again</button>
        </div>
      ) : billing && status ? (
        <>
          <div className="r42-billing-grid">
            <article className="r42-billing-card r42-billing-card--primary">
              <div className="r42-billing-card-head">
                <div>
                  <span className="r42-eyebrow">Current plan</span>
                  <h2>{planLabel(billing.plan_id)}</h2>
                </div>
                <span className={`r42-billing-status r42-billing-status--${status.tone}`}>{status.label}</span>
              </div>
              <p className="r42-billing-status-detail">{status.detail}</p>
              <dl className="r42-billing-details">
                <div><dt>Billing cadence</dt><dd>{intervalLabel(billing.billing_interval)}</dd></div>
                <div><dt>Current period ends</dt><dd>{formatDate(billing.current_period_end)}</dd></div>
                <div><dt>Provider</dt><dd>{billing.provider === 'stripe' ? 'Stripe' : billing.provider}</dd></div>
                {billing.cancel_at_period_end ? <div><dt>Renewal</dt><dd>Ends after current period</dd></div> : null}
              </dl>
            </article>

            <article className="r42-billing-card r42-billing-card--action">
              <span className="r42-eyebrow">Payment management</span>
              <h2>{billing.can_start_checkout
                ? 'Set up subscription billing'
                : !billing.checkout_enabled && !billing.billing_configured
                  ? 'Subscription billing is not open yet'
                  : 'Manage billing'}</h2>
              <p>{billing.can_start_checkout
                ? 'Continue to Stripe Checkout to securely enter payment information and start recurring billing.'
                : !billing.checkout_enabled && !billing.billing_configured
                  ? 'Route42 has prepared secure billing, but new production subscriptions are intentionally disabled until commercial pricing is activated.'
                  : billing.can_manage_billing
                    ? 'Open Stripe Customer Portal to update payment methods, review invoices, and manage available subscription settings.'
                    : 'Billing management is not available for the current billing state.'}</p>

              {actionError ? <div className="r42-form-alert r42-billing-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Billing action failed.</strong><span>{errorText(actionError)}</span></div></div> : null}

              {billing.can_start_checkout && auth.can(permissions.billingManage) ? (
                <button className="btn r42-btn-primary r42-billing-action" disabled={busy} onClick={() => void startCheckout()}>
                  {checkoutMutation.isPending ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-credit-card" aria-hidden="true" />}
                  Continue to Stripe Checkout
                </button>
              ) : billing.can_manage_billing && auth.can(permissions.billingManage) ? (
                <button className="btn r42-btn-primary r42-billing-action" disabled={busy} onClick={() => void openPortal()}>
                  {portalMutation.isPending ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-box-arrow-up-right" aria-hidden="true" />}
                  Open Customer Portal
                </button>
              ) : null}
              <small className="r42-billing-security-note"><i className="bi bi-lock" aria-hidden="true" /> Payment details are collected and stored by Stripe, not Route42.</small>
            </article>
          </div>

          {(billing.status === 'PAST_DUE' || billing.status === 'PAYMENT_ACTION_REQUIRED') ? (
            <div className="r42-billing-continuity">
              <i className="bi bi-shield-check" aria-hidden="true" />
              <div><strong>Route42 access remains available.</strong><span>{graceMessage(billing.grace_period_end)} Billing state and tenant lifecycle are separate; Route42 does not automatically suspend the manufacturing workspace.</span></div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
